import {
  BubbleChart as IconBubbleChart,
  Delete as IconDelete,
  GetApp as IconGetApp,
  Person as IconPerson
} from '@mui/icons-material';
import ActionOnSelectedItems, {
  ActionConfig
} from 'Components/Shared/ActionOnSelectedItems/ActionOnSelectedItems';
import CurrentPerson from 'Context/CurrentPerson';
import useTranslate from 'Hooks/useTranslate';
import TestIds from 'Tests/TestIds';
import AddSubscriptions from './AddSubscriptions';
import ExportUsers from './ExportUsers';
import RemoveUsers from './RemoveUsers';

interface Props {
  selectedIds: string[];
  selectAllEnabled: boolean;
  selectedCount: number;
  totalCount: number;
  onReloadUsers: () => void;
  disabled: boolean;
  searchTerm: string;
  onSelect: (ids: string[] | null) => void;
  loading: boolean;
}

function ActionOnSelectedUsers(props: Readonly<Props>) {
  const ts = useTranslate();
  const {
    selectedCount,
    totalCount,
    selectedIds,
    selectAllEnabled,
    onReloadUsers,
    onSelect,
    disabled,
    searchTerm,
    loading
  } = props;

  return (
    <CurrentPerson.Consumer>
      {({ currentPersonRoles }: CurrentPersonContext) => {
        const actions: ActionConfig[] = [
          ...(currentPersonRoles.isAccountAdmin
            ? [
              {
                key: 'addSubscriptions',
                icon: <IconBubbleChart />,
                tooltip: ts('common.action.subscribe'),
                testId: TestIds.pages.users.subscriptionButton,
                disabled,
                renderMode: 'accordion' as const,
                renderAccordionContent: (onCollapse: () => void) => (
                  <AddSubscriptions
                    selectedUserIds={selectedIds}
                    selectAllUserEnabled={selectAllEnabled}
                    selectedUserCount={selectedCount}
                    userFilter={searchTerm}
                    onCollapse={onCollapse}
                  />
                )
              },
              {
                key: 'removeUsers',
                icon: <IconDelete />,
                tooltip: ts('common.action.delete'),
                testId:
                  TestIds.pages.users.actionOnSelectedUsers.removeUsersButton,
                disabled,
                renderMode: 'popper' as const,
                renderPopperContent: (onCollapse: () => void) => (
                  <RemoveUsers
                    selectedUserIds={selectedIds}
                    selectAllUserEnabled={selectAllEnabled}
                    selectedUserCount={selectedCount}
                    userFilter={searchTerm}
                    onCollapse={onCollapse}
                    onReloadUsers={onReloadUsers}
                  />
                )
              }
            ]
            : []),
          {
            key: 'exportUsers',
            icon: <IconGetApp />,
            tooltip: ts('common.action.export'),
            disabled,
            renderMode: 'accordion' as const,
            renderAccordionContent: (onCollapse: () => void) => (
              <ExportUsers
                selectedUserIds={selectedIds}
                searchTerm={searchTerm}
                selectAllUserEnabled={selectAllEnabled}
                onCollapse={onCollapse}
              />
            )
          }
        ];

        return (
          <ActionOnSelectedItems
            selectedIds={selectedIds}
            selectAllEnabled={selectAllEnabled}
            selectedCount={selectedCount}
            totalCount={totalCount}
            onSelect={onSelect}
            disabled={disabled}
            loading={loading}
            searchTerm={searchTerm}
            icon={<IconPerson color="primary" />}
            selectionLabel={ts('page.users.index.selectedUserCount', {
              count: selectedCount,
              totalCount: totalCount
            })}
            allSelectedLabel={ts('page.users.index.allUsersSelected', {
              totalCount: totalCount
            })}
            actions={actions}
          />
        );
      }}
    </CurrentPerson.Consumer>
  );
}

export default ActionOnSelectedUsers;
